import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import LanguageContext from "../helper/LanguageContext";
import { callAPI } from "../helper/apiUtils";
import { $crud } from "../helper/CrudFactory";
import { $user } from "../helper/UserFactory";
import {
  clearDirectMessageRequest,
  requestDirectMessage,
  subscribeDirectMessage,
} from "../helper/directMessagePrompt";
import { openChatMessage } from "../pages/Cockpit/cockpitNavigation";
import {
  SET_ACTIVITY_MESSAGES,
  SET_ACTIVITY_MESSAGES_LAST_FETCHED,
} from "../store";

// Matches --kj-dur-fast, the exit animation of the prompt.
const LEAVE_MS = 150;

function personName(person) {
  if (!person) return "";
  const named = person.fullName || `${person.firstName || ""} ${person.lastName || ""}`.trim();
  if (!named || named === person.id) return "";
  return named;
}

function DirectMessagePrompt() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { strings } = useContext(LanguageContext);
  const me = useSelector((state) => state.user);
  const activityMessages = useSelector((state) => state.activityMessages || []);
  const meRef = useRef(me);
  const activityRef = useRef(activityMessages);
  const historyRef = useRef(history);
  meRef.current = me;
  activityRef.current = activityMessages;
  historyRef.current = history;

  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const leaveTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(leaveTimerRef.current), []);

  // Fade the prompt out instead of unmounting it in one frame.
  const dismiss = (req, accepted) => {
    clearDirectMessageRequest(req);
    req.resolve(accepted);
    setLeaving(true);
    clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(() => {
      setRequest((current) => (current === req ? null : current));
      setLeaving(false);
      setBusy(false);
    }, LEAVE_MS);
  };

  useEffect(() => {
    return subscribeDirectMessage((next) => {
      const currentId = meRef.current?.id || $user.get()?.id;
      if (currentId && String(next.user?.id) === String(currentId)) {
        clearDirectMessageRequest(next);
        next.resolve(false);
        historyRef.current.push("/");
        return;
      }
      clearTimeout(leaveTimerRef.current);
      setBusy(false);
      setLeaving(false);
      setRequest(next);
    });
  }, []);

  useEffect(() => {
    const onClick = (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target?.closest?.('a[href*="/user-profile/"]');
      if (!anchor) return;
      const match = (anchor.getAttribute("href") || "").match(/\/user-profile\/([^/?#]+)/);
      if (!match) return;
      event.preventDefault();
      event.stopPropagation();
      const id = decodeURIComponent(match[1]);
      const fullName = (anchor.textContent || "").replace(/\s+/g, " ").trim();
      requestDirectMessage({ id, fullName });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  const close = (accepted) => {
    if (!request || busy || leaving) return;
    dismiss(request, accepted);
  };

  const confirm = async () => {
    if (!request || busy || leaving) return;
    const person = request.user || {};
    const current = meRef.current || $user.get();
    if (!current?.id || !person.id) {
      close(false);
      return;
    }
    setBusy(true);
    try {
      const { data } = await callAPI("/group", {}, "POST", {
        groupType: "user",
        isOneToOne: true,
        users: [current.id, person.id],
      });
      const chatData = data?.chat;
      if (!chatData?.id) {
        $crud.notify({
          type: "error",
          message: data?.message || strings.error_creating_chat,
        });
        dismiss(request, false);
        return;
      }

      const userName = personName(person) || chatData.name;
      const activityMessageEntry = {
        id: chatData.id,
        mainId: chatData.id,
        group: {
          ...chatData,
          id: chatData.id,
          name: userName,
          isOneToOne: true,
          defaultChatGroup: Boolean(chatData.defaultChatGroup),
          isInternal: Boolean(chatData.isInternal),
        },
        senderId: person.id,
        senderName: userName,
        senderAvatar: person.avatar || null,
        name: userName,
        avatar: person.avatar || null,
        isOpened: true,
        isOneToOne: true,
        unseenChatCount: 0,
        createdAt: new Date().toISOString(),
      };
      // Put the new chat at the top of the list first, so the Messages screen
      // opens it by clicking its row, the same way as a chat from the cockpit.
      const updatedMessages = (activityRef.current || [])
        .filter((msg) => msg.group?.id !== chatData.id)
        .map((msg) => ({ ...msg, isOpened: false }));
      dispatch({ type: SET_ACTIVITY_MESSAGES, messages: [activityMessageEntry, ...updatedMessages] });
      dispatch({ type: SET_ACTIVITY_MESSAGES_LAST_FETCHED, payload: Date.now() });

      const nav = request.options?.replace
        ? { push: (location) => history.replace(location) }
        : history;
      openChatMessage(dispatch, nav, {
        chatGroupId: String(chatData.id),
        isOneToOne: true,
        isInternal: Boolean(chatData.isInternal),
        name: userName,
      });

      // The prompt fades out over the Messages screen as the chat opens.
      dismiss(request, true);
    } catch (error) {
      $crud.notify({
        type: "error",
        message: strings.error_creating_chat,
      });
      dismiss(request, false);
    }
  };

  if (!request) return null;
  const name = personName(request.user);

  return (
    <div
      className={`create-message-modal-overlay dm-prompt-overlay${leaving ? " is-leaving" : ""}`}
      onClick={() => close(false)}
    >
      <div className="create-message-modal dm-prompt-card" onClick={(event) => event.stopPropagation()}>
        <h4 className="create-message-modal-title">
          {strings.start_direct_message || "Start direct message?"}
        </h4>
        {name ? <p className="create-message-modal-text">{name}</p> : null}
        <div className="create-message-modal-buttons">
          <button
            type="button"
            onClick={() => close(false)}
            disabled={busy}
            className="kj-btn kj-btn--primary create-message-modal-btn"
          >
            {strings.no}
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={busy}
            aria-busy={busy || undefined}
            className="kj-btn kj-btn--primary create-message-modal-btn"
          >
            {busy ? <span className="dm-prompt-spinner" aria-hidden="true" /> : null}
            {strings.yes}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DirectMessagePrompt;
